/**
 * +glide: Mossvale's sky puzzles (docs/design/zones.md "Gliding"): four little routes of floating
 * platforms beside a wind chute (an updraft), each with a richer secret chest on top.
 *
 * Every puzzle follows the same shape, scaled to its place: three gentle hops up a stair of ledges,
 * a hop into the chute (it opens the glider and lifts you), a glide out to the top tier, one more
 * hop, a glide across a gap to the chest's ledge. Heights are absolute (the anchor's floor + offsets),
 * computed once when the zone is built (after the roads are cut into the terrain).
 *
 *   Hollowcrown Boughs   beginner, in the meadow between the great oak and town (+ Granny Pell's note)
 *   Silverstep Ledges    wooden scaffolds at the foot of Greyhorn's cliffs, west of the falls
 *   Wisp Isle Skyway     mossy rocks over Glimmermere, the chest high above Wisp Isle
 *   Starfall Spire       floating crystals over the Pale Rise's summit, by the lookout (holds a Grid Key)
 */
import type { FieldSecretDef, PlatformDef, SkyPuzzleDef, UpdraftDef, Vec2 } from '@keystone/sim';

interface PuzzleSpec {
  id: string;
  name: string;
  /** Ground start (the anchor) and the heading of the stair (radians). */
  anchor: Vec2;
  heading: number;
  /** Side the top tier turns to (+1 left of the heading, -1 right). */
  side: 1 | -1;
  kind: string;
  /** Chute height over the anchor's floor; the glide out (sideways) and the last gap (yd). */
  chuteTop: number;
  gap1: number;
  gap2: number;
  /** Turn of the last gap from the heading (radians, toward `side`). */
  turn: number;
}

export const SKY_SPECS: readonly PuzzleSpec[] = [
  { id: 'hollowcrown', name: 'Hollowcrown Boughs', anchor: { x: 396, y: 205 }, heading: 3.53, side: -1, kind: 'rock', chuteTop: 18, gap1: 9, gap2: 8, turn: 0.6 },
  { id: 'silverstep', name: 'Silverstep Ledges', anchor: { x: 667, y: 488 }, heading: 5.89, side: 1, kind: 'scaffold', chuteTop: 32, gap1: 12, gap2: 11, turn: 0.4 },
  { id: 'wisp', name: 'Wisp Isle Skyway', anchor: { x: 704, y: 240 }, heading: 0, side: 1, kind: 'rock', chuteTop: 24, gap1: 12, gap2: 14, turn: -0.35 },
  { id: 'starfall', name: 'Starfall Spire', anchor: { x: 438, y: 642 }, heading: Math.PI / 2, side: 1, kind: 'crystal', chuteTop: 30, gap1: 13, gap2: 12, turn: 0.5 },
];

/** Grid Key chest (packages/content grid GRID_KEY_SOURCES.secrets). */
export const SKY_KEY_SECRET = 'sky_starfall';

/** The hop's reach (yd) up `dz` at run speed (the sim's free-flight hop: 8.33 yd/s up, g 27.8). */
const hopReach = (dz: number): number => {
  const v = (4 * 1.25) / 0.6;
  const g = (8 * 1.25) / 0.36;
  return 7 * ((v + Math.sqrt(v * v - 2 * g * dz)) / g);
};
const GLIDE_RATIO = 8 / 3;

export interface Skyways {
  platforms: PlatformDef[];
  updrafts: UpdraftDef[];
  puzzles: SkyPuzzleDef[];
  secrets: FieldSecretDef[];
  /** Grounds the zone keeps clear of trees (chute bases, the stair). */
  keep: { pos: Vec2; r: number }[];
}

const r2 = (v: number): number => Math.round(v * 100) / 100;

/** Build the puzzles on the zone's floor (call after the roads are registered). */
export function buildSkyways(floorAt: (x: number, y: number) => number, specs: readonly PuzzleSpec[] = SKY_SPECS): Skyways {
  const out: Skyways = { platforms: [], updrafts: [], puzzles: [], secrets: [], keep: [] };
  for (const s of specs) {
    const H0 = floorAt(s.anchor.x, s.anchor.y);
    const f = { x: Math.cos(s.heading), y: Math.sin(s.heading) };
    const side = { x: -f.y * s.side, y: f.x * s.side };
    const at = (df: number, ds: number): Vec2 => ({ x: r2(s.anchor.x + f.x * df + side.x * ds), y: r2(s.anchor.y + f.y * df + side.y * ds) });
    const pid = (n: string): string => `${s.id}_${n}`;
    const plats: PlatformDef[] = [];
    const add = (n: string, pos: Vec2, top: number, r: number, kind = s.kind): PlatformDef => {
      const p: PlatformDef = { id: pid(n), pos, r, top: r2(top), kind, puzzle: s.id };
      plats.push(p);
      return p;
    };
    // The stair: three gentle hops (+0.9, +0.7, +0.7), ~4 yd apart.
    const s1 = add('step1', at(4.2, 0), H0 + 0.9, 1.8);
    const s2 = add('step2', at(8.2, 0.3), s1.top + 0.7, 1.7);
    const s3 = add('step3', at(12.2, 0.6), s2.top + 0.7, 1.8);
    // The chute, a hop beyond the stair's last step.
    const chutePos = at(16.6, 0.6);
    const chuteTop = H0 + s.chuteTop;
    const chute: UpdraftDef = { id: pid('chute'), pos: chutePos, r: 2.6, base: r2(floorAt(chutePos.x, chutePos.y)), top: r2(chuteTop) };
    // The top tier: a glide out of the chute (sideways), a hop, a glide across the last gap.
    const t1 = add('tier1', at(16.6, 0.6 + s.gap1), chuteTop - 1 - s.gap1 / GLIDE_RATIO - 2.5, 2.5);
    const hop = hopReach(0.5) - 0.6;
    const t2 = add('tier2', { x: r2(t1.pos.x + f.x * hop), y: r2(t1.pos.y + f.y * hop) }, t1.top + 0.5, 2.1);
    const dir = { x: Math.cos(s.heading + s.turn * s.side), y: Math.sin(s.heading + s.turn * s.side) };
    const gapAt = { x: r2(t2.pos.x + dir.x * s.gap2), y: r2(t2.pos.y + dir.y * s.gap2) };
    const top = add('top', gapAt, t2.top + 1.25 - s.gap2 / GLIDE_RATIO - 2, 2.8, s.kind === 'scaffold' ? 'ruin' : s.kind === 'rock' ? 'crystal' : s.kind);
    out.platforms.push(...plats);
    out.updrafts.push(chute);
    const chestId = `sky_${s.id}`;
    out.secrets.push({ id: chestId, pos: { ...top.pos }, kind: 'chest', z: top.top, sky: true });
    out.puzzles.push({
      id: s.id,
      name: s.name,
      topId: top.id,
      chestId,
      start: { ...s.anchor },
      route: [
        { to: s1.id, via: 'jump' },
        { to: s2.id, via: 'jump' },
        { to: s3.id, via: 'jump' },
        { to: t1.id, via: 'updraft', updraft: chute.id },
        { to: t2.id, via: 'jump' },
        { to: top.id, via: 'glide' },
      ],
    });
    out.keep.push({ pos: s.anchor, r: 6 }, { pos: s2.pos, r: 4 }, { pos: chutePos, r: 7 });
  }
  return out;
}
